import type { NuxtApp } from "#app";
import type { AxiosInstance, AxiosResponse } from "axios";
import type { useForm } from "laravel-precognition-vue";
import { UserModel } from "~/models/User.model";
import { useAuthStore } from "~/stores/auth";

export function createAuthService(axios: AxiosInstance, app: NuxtApp) {
  return {
    getAuthenticatedUser(): Promise<AxiosResponse> {
      const xhr = axios.get("/account-management/profile");
      // xhr.then(r => {
      //   axios.defaults.headers.common['X-CSRF-TOKEN'] =
      //     r.headers['x-csrf-token'];
      // });
      return xhr;
    },
    async checkIfCanManageUserAccount() {
      return axios.get("/account-management/can-edit").catch((e) => {
        if (e.response.status === 423) {
          useAuthStore().setRequirePasswordConfirmation(true);
        }
      });
    },
    updateUserAccountProfile(data = null) {
      return new Promise((resolve, reject) => {
        if (data) {
          this.setUser(new UserModel(data));
          resolve(data);
          return;
        }
        this.checkIfCanManageUserAccount()
          .then((r) => {
            if (r.data.data) {
              this.setUser(new UserModel(r.data.data));
              resolve(r.data.data);
            }
          })
          .catch((e) => reject(e));
      });
    },
    refreshAuthenticatedUserData(data = null) {
      return new Promise((resolve, reject) => {
        this.getAuthenticatedUser()
          .then((r) => {
            if (r.data.data) {
              const user = new UserModel(r.data.data);
              this.setUser(user);
              this.setRedirectTo(r.data.redirect_to);
              resolve(user);
            }
          })
          .catch((e) => reject(e));
      });
    },
    /** @returns {Promise<AxiosResponse>} */
    performLogin(
      form: ReturnType<
        typeof useForm<{
          email: string;
          password: string;
          remember: boolean;
          "g-recaptcha-response": string;
        }>
      >,
    ): Promise<AxiosResponse> {
      return form.submit(("/auth/login" + location.search) as any) as Promise<AxiosResponse>;
    },
    logout() {
      return new Promise((resolve, reject) => {
        axios
          .post("/auth/logout" + location.search)
          .then((r) => {
            this.setUser(new UserModel({}));
            resolve(r);
          })
          .catch((e) => reject(e));
      });
    },
    /**
     * The reason is required by the API. It is the last chance to learn
     * anything from a departure — the account is anonymized in the same
     * request, so there is nobody left to ask afterwards.
     */
    closeAccount(confirm_text: string, reason_code: string, reason_details?: string) {
      return axios.delete("/account-management/close-account", {
        data: {
          confirm_text,
          reason_code,
          reason_details: reason_details || undefined,
        },
      });
    },
    /** @returns {Promise<AxiosResponse>} */
    confirm(password: string) {
      return axios.post("/auth/password/confirm", { password });
    },
    setUser(user: UserModel) {
      useAuthStore().setUser(user);
    },
    getUser() {
      return useAuthStore().getUser();
    },
    setRedirectTo(redirectTo: string) {
      useAuthStore().setRedirectTo(redirectTo);
    },
  };
}

export default defineNuxtPlugin({
  name: "authService",
  dependsOn: ["axios"],
  async setup(nuxtApp) {
    const axios = nuxtApp.$axios as AxiosInstance;
    const app = nuxtApp;
    nuxtApp.provide("authService", createAuthService(axios, app));
  },
});
